import React, { useState, useEffect, useRef } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { getLeaseRecordsApiErrorMessage } from '../LeaseRecordsApiError';
import { Loader2, X, AlertTriangle, Save, Plus, Calendar } from 'src/components/common/icons';
import { DateField, Field } from './Master';

interface IExtendDueDateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    liTg: number;
}

interface ILeaseDueDate {
    liTg: number;
    extendedDate: string; // Key
    oldDueDate?: string;
    newDueDate?: string;
    oldDueDate2?: string; // Old Inv. Due Date
    newDueDate2?: string; // New Inv. Due Date
    reason: string;
}

const LeaseDueDateModal: React.FC<IExtendDueDateModalProps> = ({ isOpen, onClose, onSave, liTg }) => {
    const { get, post, response } = useFetch();
    const isMounted = useRef(false);

    // --- State ---
    const [history, setHistory] = useState<ILeaseDueDate[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // Form State
    const [isCreating, setIsCreating] = useState(false);
    const [formData, setFormData] = useState<Partial<ILeaseDueDate>>({});

    // --- Lifecycle ---
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        if (isOpen && liTg) {
            loadData();
        } else {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, liTg]);

    // --- Actions ---

    const resetForm = () => {
        setHistory([]);
        setErrorMsg(null);
        setIsCreating(false);
        setFormData({});
    };

    const loadData = async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const data = await get(`/v1.0/leases/invoices/${liTg}/due-dates`);
            if (isMounted.current) {
                if (response.ok) {
                    setHistory(data || []);
                } else {
                    setErrorMsg('Failed to load due date history.');
                }
            }
        } catch (e) {
            if (isMounted.current) {
                setErrorMsg('System error while loading data.');
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleCreateNew = () => {
        setIsCreating(true);
        setFormData({
            liTg: liTg,
            // Only New dates and Reason are needed for Create
            newDueDate: '',
            newDueDate2: '',
            reason: '',
        });
        setErrorMsg(null);
    };

    const handleCancel = () => {
        setIsCreating(false);
        setFormData({});
        setErrorMsg(null);
    };

    const handleSave = async () => {
        if (!formData.newDueDate) {
            setErrorMsg('New Due Date is required.');
            return;
        }
        if (!formData.newDueDate2) {
            setErrorMsg('New Inv. Due Date is required.');
            return;
        }
        if (!formData.reason) {
            setErrorMsg('Reason is required.');
            return;
        }

        setLoading(true);
        setErrorMsg(null);

        try {
            await post('/v1.0/leases/invoices/due-dates', formData);
            onSave();

            if (isMounted.current) {
                if (response.ok) {
                    setIsCreating(false);
                    setFormData({});
                    await loadData();
                } else {
                    const data = response.data;
                    setErrorMsg(getLeaseRecordsApiErrorMessage(data || response.data, response.status));
                }
            }
        } catch (e) {
            if (isMounted.current) {
                setErrorMsg('System error occurred during save.');
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    // --- Helpers ---
    const formatDate = (dateStr?: string) => {
        if (!dateStr) {
            return '-';
        }
        return new Date(dateStr).toLocaleDateString();
    };

    // --- Render Form ---
    const renderForm = () => (
        <div style={styles.formContainer}>
            <div style={styles.formHeader}>
                <h4 style={styles.formTitle}>Extend Due Dates</h4>
            </div>

            <div style={styles.row}>
                {/* For create, Old dates are handled by SP, so we only show New fields */}
                <div style={{ flex: 1 }}>
                    <DateField
                        label="New Due Date"
                        value={formData.newDueDate || ''}
                        onChange={(v) => setFormData((prev) => ({ ...prev, newDueDate: v }))}
                        editable={true}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <DateField
                        label="New Inv. Due Date"
                        value={formData.newDueDate2 || ''}
                        onChange={(v) => setFormData((prev) => ({ ...prev, newDueDate2: v }))}
                        editable={true}
                    />
                </div>
            </div>

            <div style={styles.row}>
                <div style={{ flex: 1 }}>
                    <Field
                        label="Reason"
                        value={formData.reason || ''}
                        onChange={(v) => setFormData((prev) => ({ ...prev, reason: v }))}
                        editable={true}
                    />
                </div>
            </div>

            <div style={styles.formActions}>
                <button onClick={handleCancel} style={styles.btnCancel} disabled={loading}>
                    Cancel
                </button>
                <button onClick={handleSave} style={styles.btnSave} disabled={loading}>
                    {loading ? <Loader2 style={styles.spinner} /> : <Save style={{ width: '14px', marginRight: '5px' }} />}
                    Save
                </button>
            </div>
        </div>
    );

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>
                        <Calendar style={styles.headerIcon} />
                        Extend Due Date
                    </h2>
                    <button onClick={onClose} style={styles.closeButton} aria-label="Close">
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.modalBody}>
                    {errorMsg && (
                        <div style={styles.errorState}>
                            <AlertTriangle style={styles.errorIcon} />
                            {errorMsg}
                        </div>
                    )}

                    {/* Toolbar */}
                    {!isCreating && (
                        <div style={styles.toolbar}>
                            <button onClick={handleCreateNew} style={styles.btnAdd}>
                                <Plus style={{ width: '14px', marginRight: '5px' }} />
                                Add Extension
                            </button>
                        </div>
                    )}

                    {/* Form Section */}
                    {isCreating && renderForm()}

                    {/* Data Table */}
                    <div style={styles.tableWrapper}>
                        <table style={styles.table}>
                            <thead style={styles.tableHead}>
                                <tr>
                                    <th style={styles.tableHeaderCell}>Date Extended</th>
                                    <th style={styles.tableHeaderCell}>Old Due Date</th>
                                    <th style={styles.tableHeaderCell}>New Due Date</th>
                                    <th style={styles.tableHeaderCell}>Old Inv. Due Date</th>
                                    <th style={styles.tableHeaderCell}>New Inv. Due Date</th>
                                    <th style={styles.tableHeaderCell}>Reason</th>
                                </tr>
                            </thead>
                            <tbody style={styles.tableBody}>
                                {history.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} style={styles.emptyState}>
                                            No extension history found.
                                        </td>
                                    </tr>
                                ) : (
                                    history.map((h, idx) => (
                                        <tr key={`${h.liTg}-${h.extendedDate}-${idx}`} style={styles.tableRow}>
                                            <td style={styles.tableDataCell}>{formatDate(h.extendedDate)}</td>
                                            <td style={styles.tableDataCell}>{formatDate(h.oldDueDate)}</td>
                                            <td style={styles.tableDataCell}>{formatDate(h.newDueDate)}</td>
                                            <td style={styles.tableDataCell}>{formatDate(h.oldDueDate2)}</td>
                                            <td style={styles.tableDataCell}>{formatDate(h.newDueDate2)}</td>
                                            <td style={styles.tableDataCell}>{h.reason}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 100,
        backdropFilter: 'blur(2px)',
    },
    modalContent: {
        backgroundColor: '#fff',
        borderRadius: '0.75rem',
        width: '95%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        fontFamily: 'inherit',
    },
    modalHeader: {
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid #E5E7EB',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#FFFFFF',
        borderRadius: '0.75rem 0.75rem 0 0',
    },
    modalTitle: { margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    headerIcon: { width: '1.5rem', height: '1.5rem', color: '#4F46E5' },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },

    modalBody: { padding: '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', flexGrow: 1, backgroundColor: '#F9FAFB' },

    toolbar: { display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' },
    btnAdd: {
        padding: '0.5rem 1rem',
        backgroundColor: '#4F46E5',
        color: '#fff',
        border: 'none',
        borderRadius: '0.375rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.875rem',
        fontWeight: 500,
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'background-color 0.15s ease-in-out',
    },

    formContainer: {
        padding: '1.25rem',
        backgroundColor: '#FFFFFF',
        borderRadius: '0.5rem',
        border: '1px solid #E5E7EB',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        marginBottom: '1.5rem',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    },
    formHeader: { marginBottom: '0.5rem', borderBottom: '1px solid #F3F4F6', paddingBottom: '0.5rem' },
    formTitle: { margin: 0, fontSize: '1rem', fontWeight: 600, color: '#374151' },
    row: { display: 'flex', gap: '1.5rem' },

    formActions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' },
    btnSave: {
        padding: '0.5rem 1rem',
        backgroundColor: '#10B981',
        color: '#fff',
        border: 'none',
        borderRadius: '0.375rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.875rem',
        fontWeight: 500,
    },
    btnCancel: {
        padding: '0.5rem 1rem',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        border: '1px solid #D1D5DB',
        borderRadius: '0.375rem',
        cursor: 'pointer',
        fontSize: '0.875rem',
        fontWeight: 500,
    },

    tableWrapper: {
        border: '1px solid #E5E7EB',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
    tableHead: { backgroundColor: '#F3F4F6', borderBottom: '1px solid #E5E7EB' },
    tableHeaderCell: {
        padding: '0.75rem 1rem',
        textAlign: 'left',
        fontWeight: 600,
        color: '#6B7280',
        textTransform: 'uppercase',
        fontSize: '0.75rem',
        letterSpacing: '0.05em',
    },
    tableBody: { backgroundColor: '#FFFFFF' },
    tableRow: { borderBottom: '1px solid #F3F4F6', transition: 'background-color 0.1s' },
    tableDataCell: { padding: '0.75rem 1rem', color: '#1F2937', verticalAlign: 'middle' },
    emptyState: { textAlign: 'center', padding: '3rem', color: '#9CA3AF', fontStyle: 'italic' },

    actionIcons: { display: 'flex', gap: '0.5rem', justifyContent: 'center' },
    iconBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#4F46E5',
        padding: '0.25rem',
        borderRadius: '0.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color 0.15s',
    },
    iconBtnDelete: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#EF4444',
        padding: '0.25rem',
        borderRadius: '0.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color 0.15s',
    },

    errorState: {
        display: 'flex',
        alignItems: 'center',
        padding: '1rem',
        marginBottom: '1rem',
        color: '#B91C1C',
        fontWeight: 500,
        backgroundColor: '#FEE2E2',
        borderRadius: '0.5rem',
        border: '1px solid #FECACA',
    },
    errorIcon: { width: '1.25rem', height: '1.25rem', marginRight: '0.75rem' },
    spinner: { width: '0.9rem', height: '0.9rem', animation: 'spin 1s linear infinite', color: '#FFFFFF', marginRight: '4px' },
};

export default LeaseDueDateModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/LeaseDueDateModal.tsx